import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { productDb } from "../firebaseProducts";

const COLLECTIONS = [
  { name: "adult-list-menu", audience: "adults", category: "apparel" },
  { name: "adult-accessories-list-menu", audience: "adults", category: "accessories" },
  { name: "drinkwear-menu", audience: "adults", category: "accessories" },
  { name: "kids-apparel-menu", audience: "kids", category: "apparel" },
  { name: "kids-apparel-white-menu", audience: "kids", category: "apparel" },
  { name: "kids-accessories-menu", audience: "kids", category: "accessories" }
];

const priceRanges = {
  "<50": [0, 49.99],
  "50-100": [50, 100],
  ">100": [100.01, Infinity]
};

const embellishmentOnlyBrands = [
  "Brooks Brothers",
  "New Era",
  "Outdoor Research",
  "The North Face",
  "Carhartt",
  "Nike",
  "Stanley/Stella",
  "Tommy Bahama",
  "Cotopaxi",
  "OGIO",
  "tentree",
  "Travis Mathew",
  "Eddie Bauer"
];

// ---------- Gender detection only for adult apparel ----------
function detectGenderFromTags(tags) {
  if (!tags) return "unisex";

  let tagString = Array.isArray(tags)
    ? tags.join(",")
    : typeof tags === "string"
    ? tags
    : "";

  const t = tagString
    .split(",")
    .map(x => x.trim().toLowerCase())
    .filter(Boolean);

  if (t.some(x => x.includes("men") || x.includes("male") || x.includes("man")))
    return "men";
  if (t.some(x => x.includes("women") || x.includes("lady") || x.includes("female")))
    return "women";

  return "unisex";
}

export default function BundleSelector() {
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({
    priceRange: null,
    audience: null,
    gender: null,
    type: null,
    pieces: null,
    noDecoration: false
  });

  const [results, setResults] = useState([]);
  const [reveal, setReveal] = useState(false);

  // ---------- Load products ----------
  useEffect(() => {
    const fetchAll = async () => {
      let all = [];
      for (const c of COLLECTIONS) {
        const snap = await getDocs(collection(productDb, c.name));
        const items = snap.docs.map(d => {
          const data = d.data();
          const tags = data.Tags || data.Tag || null;

          return {
            id: d.id,
            collection: c.name,
            audience: c.audience,
            category: c.category,
            title: data.Title || data.Title2 || "Untitled",
            price: Number(data.Price || data.SUGGESTED_PRICE || 0),
            image:
              data.img ||
              data.front ||
              data.VariantImage ||
              data.ImageSrc ||
              data.FRONT_FLAT ||
              null,
            tags,
            vendor: data.Vendor || "",
            gender:
              c.category === "apparel" && c.audience === "adults"
                ? detectGenderFromTags(tags)
                : "unisex",
            style: data.Style || data.SKU || data.Model || d.id
          };
        });
        all = all.concat(items);
      }
      setProducts(all);
    };
    fetchAll();
  }, []);

  // ---------- Watch filters ----------
  useEffect(() => {
    if (!products.length) return;
    if (!filters.priceRange || !filters.audience || !filters.type || !filters.pieces) return;
    if ((filters.audience === "adults" || filters.audience === "mix") && !filters.gender) return;
    generateBundles();
  }, [filters, products]);

  const shuffle = arr => [...arr].sort(() => Math.random() - 0.5);

  // ---------- Generate bundles ----------
  const generateBundles = () => {
    const [min, max] = priceRanges[filters.priceRange];
    let pool = [];

    // Audience
    if (filters.audience === "kids") pool = products.filter(p => p.audience === "kids");
    else if (filters.audience === "adults") pool = products.filter(p => p.audience === "adults");
    else pool = products;

    // Gender filter ONLY adult apparel
    if (filters.audience === "adults" || filters.audience === "mix") {
      pool = pool.filter(p => {
        if (p.category !== "apparel") return true;
        if (filters.gender === "unisex") return true;

        return p.gender === filters.gender || p.gender === "unisex";
      });
    }

    const combos = combinations(pool, filters.pieces);

    const valid = shuffle(combos)
      .map(combo => {
        const total = combo.reduce((s, p) => s + p.price, 0);
        const needsDecoration = combo.some(p =>
          embellishmentOnlyBrands.includes(p.vendor)
        );
        return { combo, total, needsDecoration };
      })
      .filter(({ combo, total, needsDecoration }) => {
        if (total < min || total > max) return false;

        // Prevent duplicate styles
        const styles = combo.map(p => p.style);
        if (new Set(styles).size !== styles.length) return false;

        // Type rule
        const cats = combo.map(p => p.category);
        if (filters.type === "mixed")
          return cats.includes("apparel") && cats.includes("accessories");
        if (!cats.every(c => c === filters.type)) return false;

        // NEW FILTER: No Decoration Mode
        if (filters.noDecoration && needsDecoration) return false;

        return true;
      })
      .slice(0, 5);

    setReveal(false);
    setTimeout(() => {
      setResults(valid);
      setReveal(true);
    }, 400);
  };

  return (
    <div style={styles.wrapper}>
      <h2 style={styles.title}>🎁 Build Your Custom Bundle</h2>

      <div style={styles.controls}>
        <Selector
          label="Price Range"
          options={[
            { v: "<50", label: "Under $50" },
            { v: "50-100", label: "$50 - $100" },
            { v: ">100", label: "Over $100" }
          ]}
          value={filters.priceRange}
          onChange={v => setFilters(f => ({ ...f, priceRange: v }))}
        />

        <Selector
          label="Audience"
          options={[
            { v: "adults", label: "Adults" },
            { v: "kids", label: "Kids" },
            { v: "mix", label: "Mix" }
          ]}
          value={filters.audience}
          onChange={v =>
            setFilters(f => ({
              ...f,
              audience: v,
              gender: v === "kids" ? null : f.gender
            }))
          }
        />

        {(filters.audience === "adults" || filters.audience === "mix") && (
          <Selector
            label="Gender"
            options={[
              { v: "men", label: "Men" },
              { v: "women", label: "Women" },
              { v: "unisex", label: "Unisex" }
            ]}
            value={filters.gender}
            onChange={v => setFilters(f => ({ ...f, gender: v }))}
          />
        )}

        <Selector
          label="Type"
          options={[
            { v: "apparel", label: "Apparel" },
            { v: "accessories", label: "Accessories" },
            { v: "mixed", label: "Mixed" }
          ]}
          value={filters.type}
          onChange={v => setFilters(f => ({ ...f, type: v }))}
        />

        <Selector
          label="Pieces"
          options={[
            { v: 1, label: "1 Item" },
            { v: 2, label: "2 Items" },
            { v: 3, label: "3 Items" }
          ]}
          value={filters.pieces}
          onChange={v => setFilters(f => ({ ...f, pieces: v }))}
        />

        {/* ===== NEW TOGGLE ===== */}
        <div style={styles.selector}>
          <div style={styles.selectorLabel}>No Decoration</div>
          <button
            style={{
              ...styles.selectorBtn,
              ...(filters.noDecoration ? styles.activeBtn : {})
            }}
            onClick={() =>
              setFilters(f => ({ ...f, noDecoration: !f.noDecoration }))
            }
          >
            {filters.noDecoration ? "ON" : "OFF"}
          </button>
        </div>
      </div>

      <hr />

      {!results.length && <p>Select options to generate bundles</p>}

      <div style={styles.bundleGrid}>
        {results.map((bundleData, i) => (
          <div
            key={i}
            style={{
              ...styles.bundleCard,
              transform: reveal ? "rotateY(0deg)" : "rotateY(180deg)"
            }}
          >
            <strong>Bundle #{i + 1}</strong>

            <div style={{ marginBottom: 6, fontWeight: "bold", color: "#555" }}>
              {bundleData.needsDecoration
                ? "Needs Decoration"
                : "Can be sold blank"}
            </div>

            <div style={styles.itemsGrid}>
              {bundleData.combo.map(p => (
                <div key={p.id} style={styles.itemCard}>
                  {p.image && (
                    <img src={p.image} alt={p.title} style={styles.image} />
                  )}
                  <div style={styles.itemTitle}>{p.title}</div>
                  <div style={styles.price}>${p.price}</div>
                </div>
              ))}
            </div>

            <div style={styles.total}>Total: ${bundleData.total.toFixed(2)}</div>
          </div>
        ))}
      </div>

      {results.length > 0 && (
        <button style={styles.refreshBtn} onClick={generateBundles}>
          🔄 Show Another 5 Bundles
        </button>
      )}
    </div>
  );
}

// ---------- Selector ----------
function Selector({ label, options, value, onChange }) {
  return (
    <div style={styles.selector}>
      <div style={styles.selectorLabel}>{label}</div>
      <div style={styles.selectorBtns}>
        {options.map(o => (
          <button
            key={o.v}
            style={{
              ...styles.selectorBtn,
              ...(value === o.v ? styles.activeBtn : {})
            }}
            onClick={() => onChange(o.v)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------- Combinations ----------
function combinations(arr, size) {
  if (size === 1) return arr.map(v => [v]);
  const res = [];
  arr.forEach((v, i) => {
    combinations(arr.slice(i + 1), size - 1).forEach(c =>
      res.push([v, ...c])
    );
  });
  return res;
}

// ---------- Styles ----------
const styles = {
  wrapper: { padding: 20, maxWidth: 900, margin: "auto" },
  title: { textAlign: "center", marginBottom: 15 },
  controls: { display: "grid", gap: 15 },
  selector: { background: "#fafafa", padding: 10, borderRadius: 10 },
  selectorLabel: { fontWeight: "bold", marginBottom: 5 },
  selectorBtns: { display: "flex", gap: 8, flexWrap: "wrap" },
  selectorBtn: {
    padding: "6px 10px",
    borderRadius: 20,
    border: "1px solid #ccc",
    background: "white",
    cursor: "pointer"
  },
  activeBtn: { background: "#111", color: "white" },
  bundleGrid: { display: "grid", gap: 15 },
  bundleCard: {
    border: "1px solid #ddd",
    padding: 12,
    borderRadius: 10,
    background: "white",
    transition: "0.6s",
    transformStyle: "preserve-3d"
  },
  itemsGrid: { display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6 },
  itemCard: {
    width: 120,
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: 6,
    textAlign: "center",
    background: "#fff"
  },
  image: { width: "100%", height: 110, objectFit: "contain", marginBottom: 6 },
  itemTitle: { fontSize: 12 },
  price: { fontWeight: "bold" },
  total: { marginTop: 8 },
  refreshBtn: {
    marginTop: 15,
    padding: "10px 15px",
    fontSize: 16,
    borderRadius: 10,
    border: "none",
    background: "#111",
    color: "white",
    cursor: "pointer"
  }
};
